import { useEffect, useMemo, useState } from "react";
import { formatMoney, type CurrencyFormat } from "../../domain/money/format";
import { money } from "../../domain/money/money";
import { refundableUnits } from "../../domain/journal/corrections";
import {
  readCorrectionPosition,
  voidSale,
  refundSale,
  CorrectionError,
  type CorrectionPosition,
} from "../../data/local/corrections";
import { getSale, getSaleLines } from "../../data/local/repositories";
import { ManagerAuth, type Authorization } from "../components/ManagerAuth";
import type { LocalSale, LocalSaleLine } from "../../data/local/db";

/**
 * Void and refund at the counter (T160, T161).
 *
 * ONE dialog for both, because from the cashier's side it is one decision with
 * two answers — "this whole sale should not exist" versus "give some of this
 * back" — and splitting it into two entry points makes staff pick before they
 * have looked at the sale.
 *
 * THE ORDER IS DELIBERATE: choose what, then say why, then get approval. Asking
 * for the manager's PIN first trains everyone to approve before reading, which
 * is how an approval control becomes a formality.
 *
 * Every path here works with no network.
 */

interface CorrectionDialogProps {
  saleId: string;
  currency: CurrencyFormat;
  staffId: string;
  onDone(message: string): void;
  onCancel(): void;
}

type Mode = "choose" | "void" | "refund";

export function CorrectionDialog({
  saleId,
  currency,
  staffId,
  onDone,
  onCancel,
}: CorrectionDialogProps) {
  const [sale, setSale] = useState<LocalSale | null>(null);
  const [lines, setLines] = useState<LocalSaleLine[]>([]);
  const [position, setPosition] = useState<CorrectionPosition | null>(null);
  const [mode, setMode] = useState<Mode>("choose");
  const [reason, setReason] = useState("");
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [isCash, setIsCash] = useState(true);
  const [authorizing, setAuthorizing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      const [loadedSale, loadedLines, loadedPosition] = await Promise.all([
        getSale(saleId),
        getSaleLines(saleId),
        readCorrectionPosition(saleId),
      ]);
      setSale(loadedSale ?? null);
      setLines(loadedLines);
      setPosition(loadedPosition);
    })();
  }, [saleId]);

  const refundTotal = useMemo(() => {
    if (!position) return money(0);
    let total = 0;
    for (const line of position.lines) {
      const wanted = quantities[line.saleLineId] ?? 0;
      if (wanted > 0) {
        total += refundableUnits(line)
          .slice(0, wanted)
          .reduce((sum, unit) => sum + unit, 0);
      }
    }
    return money(total);
  }, [position, quantities]);

  if (!sale || !position) {
    return <div className="correction" role="dialog" aria-label="Correct a sale" />;
  }

  const anySelected = Object.values(quantities).some((quantity) => quantity > 0);
  const reasonGiven = reason.trim() !== "";

  async function apply(authorization: Authorization) {
    setAuthorizing(false);
    setBusy(true);
    setError(null);

    try {
      if (mode === "void") {
        await voidSale({
          saleId,
          staffId,
          authorizedBy: authorization.managerId,
          reason: reason.trim(),
        });
        onDone(`${sale!.documentNumber} voided.`);
      } else {
        const refund = await refundSale({
          saleId,
          staffId,
          authorizedBy: authorization.managerId,
          reason: reason.trim(),
          quantities,
          isCash,
        });
        onDone(
          `${formatMoney(refund.totalAmount, currency)} refunded against ${sale!.documentNumber}.`,
        );
      }
    } catch (caught) {
      setError(
        caught instanceof CorrectionError
          ? caught.message
          : caught instanceof Error
            ? caught.message
            : "The correction could not be recorded.",
      );
    } finally {
      setBusy(false);
    }
  }

  if (authorizing) {
    return (
      <ManagerAuth
        action={mode === "void" ? "Void a sale" : "Refund a sale"}
        detail={
          mode === "void"
            ? `${sale.documentNumber} · ${formatMoney(sale.total, currency)}`
            : `${sale.documentNumber} · ${formatMoney(refundTotal, currency)} of ${formatMoney(sale.total, currency)}`
        }
        onAuthorized={(authorization) => void apply(authorization)}
        onCancel={() => setAuthorizing(false)}
      />
    );
  }

  return (
    <div className="correction" role="dialog" aria-label="Correct a sale">
      <header className="correction__head">
        <h2 className="correction__title">{sale.documentNumber}</h2>
        <p className="correction__sub">
          {formatMoney(sale.total, currency)}
          {position.refundedSoFar > 0 && (
            <> · {formatMoney(position.refundedSoFar, currency)} already refunded</>
          )}
        </p>
      </header>

      {position.status === "voided" && (
        <p className="correction__note">This sale has been voided. Nothing further can be done.</p>
      )}

      {mode === "choose" && (
        // Shown immediately, not gated behind picking Refund first — reading
        // what was actually sold shouldn't require starting a correction.
        <ul className="correction__preview">
          {lines.map((line) => (
            <li key={line.id} className="correction__preview-line">
              <span className="correction__preview-qty">{line.quantity}×</span>
              <span className="correction__preview-name">
                {line.itemNameSnapshot}
                {line.variantNameSnapshot ? ` · ${line.variantNameSnapshot}` : ""}
              </span>
              <span className="correction__preview-total money">
                {formatMoney(line.lineTotal, currency)}
              </span>
            </li>
          ))}
        </ul>
      )}

      {mode === "choose" && position.status !== "voided" && (
        <div className="correction__choose">
          <button
            type="button"
            className="correction__option"
            disabled={!position.canVoid}
            onClick={() => setMode("void")}
          >
            <span className="correction__option-title">Void the whole sale</span>
            <span className="correction__option-note">
              {position.canVoid
                ? "It should not have been rung at all."
                : "Not available — part of this sale has already been refunded."}
            </span>
          </button>

          <button
            type="button"
            className="correction__option"
            disabled={!position.canRefund}
            onClick={() => setMode("refund")}
          >
            <span className="correction__option-title">Refund some lines</span>
            <span className="correction__option-note">
              {position.canRefund
                ? "The sale stands; give part of it back."
                : "Not available — this sale is fully refunded."}
            </span>
          </button>
        </div>
      )}

      {mode === "refund" && (
        <ul className="correction__lines">
          {lines.map((line) => {
            const refundable = position.lines.find((l) => l.saleLineId === line.id);
            const remaining = refundable ? refundable.quantity - refundable.refundedQuantity : 0;
            const chosen = quantities[line.id] ?? 0;

            return (
              <li key={line.id} className="correction__line">
                <div className="correction__line-what">
                  <span className="correction__line-name">
                    {line.itemNameSnapshot}
                    {line.variantNameSnapshot ? ` · ${line.variantNameSnapshot}` : ""}
                  </span>
                  <span className="correction__line-note">
                    {remaining === 0
                      ? "Fully refunded"
                      : `${remaining} of ${line.quantity} left to refund`}
                  </span>
                </div>

                <div className="correction__stepper">
                  <button
                    type="button"
                    aria-label={`Refund one fewer ${line.itemNameSnapshot}`}
                    disabled={chosen === 0}
                    onClick={() => setQuantities({ ...quantities, [line.id]: chosen - 1 })}
                  >
                    −
                  </button>
                  <span className="correction__count">{chosen}</span>
                  <button
                    type="button"
                    aria-label={`Refund one more ${line.itemNameSnapshot}`}
                    disabled={chosen >= remaining}
                    onClick={() => setQuantities({ ...quantities, [line.id]: chosen + 1 })}
                  >
                    +
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {mode === "refund" && (
        <div className="correction__tender">
          <span className="correction__tender-label">Give it back as</span>
          <div className="correction__tender-options">
            <button
              type="button"
              className={isCash ? "is-selected" : ""}
              onClick={() => setIsCash(true)}
            >
              Cash
            </button>
            <button
              type="button"
              className={!isCash ? "is-selected" : ""}
              onClick={() => setIsCash(false)}
            >
              Original tender
            </button>
          </div>
        </div>
      )}

      {mode !== "choose" && (
        <>
          <input
            className="correction__reason"
            placeholder={mode === "void" ? "Why is this being voided?" : "Why is this refunded?"}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />

          {mode === "refund" && (
            <div className="correction__total">
              <span>Refund</span>
              <span className="money">{formatMoney(refundTotal, currency)}</span>
            </div>
          )}
        </>
      )}

      {error && (
        <p className="correction__error" role="alert">
          {error}
        </p>
      )}

      <div className="correction__actions">
        <button
          type="button"
          onClick={() => (mode === "choose" ? onCancel() : setMode("choose"))}
          disabled={busy}
        >
          {mode === "choose" ? "Cancel" : "Back"}
        </button>

        {mode !== "choose" && (
          <button
            type="button"
            className="is-primary"
            disabled={busy || !reasonGiven || (mode === "refund" && !anySelected)}
            onClick={() => setAuthorizing(true)}
          >
            {busy ? "Recording…" : "Get manager approval"}
          </button>
        )}
      </div>
    </div>
  );
}

export default CorrectionDialog;
